<template lang="pug">
.monitoragroweb-clientes
  v-row.monitoragroweb-clientes__grid(no-gutters)
    v-col.monitoragroweb-clientes__col--esquerda(cols="12", md="3")
      v-row.monitoragroweb-row
        v-col(cols="12")
          filtro-caracterizacao-card.monitoragroweb-clientes__linha-1(
            :itens="personasDisponiveis",
            :selecionados="personasSelecionadas",
            @toggle="togglePersona"
          )

      v-row.monitoragroweb-row.monitoragroweb-row--conteudo
        v-col(cols="12")
          clientes-card.monitoragroweb-clientes__flex(
            :itens="clientesFiltrados",
            :personas="personas",
            :carregando="carregandoClientes",
            :selecionado="clienteSelecionado",
            :busca="buscaCliente",
            :filtros-desabilitados="personasSelecionadas.length === 0",
            :softhouse-opcoes="softhouseOpcoes",
            :softhouse-filtro="softhouseFiltro",
            :statusbase-opcoes="statusbaseOpcoes",
            :statusbase-filtro="statusbaseFiltro",
            @select="selecionarCliente",
            @update:busca="buscaCliente = $event",
            @update:softhouse-filtro="softhouseFiltro = $event",
            @toggle-softhouse-filtro="toggleSofthouseFiltro",
            @update:statusbase-filtro="statusbaseFiltro = $event",
            @toggle-statusbase-filtro="toggleStatusbaseFiltro"
          )

    v-col.monitoragroweb-clientes__col--direita(cols="12", md="9")
      v-row.monitoragroweb-row
        v-col(cols="12")
          v-card.panel-card.panel-card--identificacao(outlined)
            .panel-card__header
              .monitoragroweb-clientes__identificacao
                .monitoragroweb-clientes__nome {{ clienteSelecionado ? clienteSelecionado.nome : "Nenhum cliente selecionado" }}
                .monitoragroweb-clientes__subtitulo {{ clienteSelecionado ? "Resumo de uso no período selecionado" : "Selecione um cliente ao lado para visualizar os dados" }}

      v-row.monitoragroweb-row
        v-col(
          v-for="card in summaryCards",
          :key="card.key",
          cols="12",
          sm="6",
          md="4",
          lg="2"
        )
          summary-card(
            :title="card.title",
            :value="card.value",
            :percentage="card.percentage",
            :variant="card.variant"
          )

      v-row.monitoragroweb-row.monitoragroweb-row--conteudo
        v-col(cols="12", md="6")
          v-card.panel-card.panel-card--lista(outlined)
            .panel-card__header
              span.panel-card__title Funcionalidades mais utilizadas
            v-card-text.panel-card__content.panel-card__content--lista
              .panel-card__empty(v-if="carregandoResumo")
                v-progress-circular(indeterminate, color="primary", size="32")
              .panel-card__empty(v-else-if="!funcionalidades.length")
                span Nenhuma funcionalidade encontrada para o período selecionado.
              template(v-else)
                .func-item(
                  v-for="item in funcionalidades",
                  :key="item.funcionalidade",
                  :class="{ 'func-item--ativo': funcionalidadeSelecionada === item.funcionalidade }",
                  @click="selecionarFuncionalidade(item)"
                )
                  .func-item__header
                    span.func-item__nome {{ item.funcionalidade }}
                    span.func-item__total {{ formatInteger(item.total) }}
                  .func-item__bar
                    .func-item__bar-fill(:style="{ width: larguraBarra(item) + '%' }")
                  .func-item__meta {{ formatInteger(item.entradas) }} entradas · {{ formatInteger(item.acoes) }} ações

        v-col(cols="12", md="6")
          v-card.panel-card.panel-card--lista(outlined)
            .panel-card__header
              span.panel-card__title Atividades recentes
            v-card-text.panel-card__content.panel-card__content--tabela
              .panel-card__empty(v-if="carregandoAtividades")
                v-progress-circular(indeterminate, color="primary", size="32")
              .panel-card__empty(v-else-if="!atividades.length")
                span Nenhuma atividade encontrada para o período selecionado.
              v-data-table(
                v-else,
                :headers="atividadesHeaders",
                :items="atividades",
                :items-per-page="-1",
                hide-default-footer,
                disable-sort,
                dense,
                locale="pt-BR",
                no-data-text="Nenhum registro encontrado",
                class="monitoragroweb-clientes__tabela"
              )
</template>

<script>
import csapi from "@/api/csapi";
import { state } from "@/socket";
import FiltroCaracterizacaoCard from "@/components/shared/FiltroCaracterizacaoCard";
import ClientesCard from "@/components/shared/ClientesCard";
import SummaryCard from "@/components/dashboards/shared/SummaryCard";
import { msParaMinutos, formatMinutos } from "@/utils/tempo";
import { CARACTERIZACAO_SLUGS } from "@/utils/caracterizacao";

const NUMBER_FORMATTER = new Intl.NumberFormat("pt-BR");
const PERCENT_FORMATTER = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1
});

const ENDPOINT_CARDS = "/v1/analytics/dashboard/cards";
const ENDPOINT_FUNCIONALIDADES = "/v1/analytics/dashboard/funcionalidades";
const ENDPOINT_ATIVIDADES_RECENTES = "/v1/analytics/dashboard/atividades-recentes";

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

function formatInteger(value) {
  return NUMBER_FORMATTER.format(Math.round(toNumber(value)));
}

function formatPercentual(valor, total) {
  const totalNumero = toNumber(total);
  if (!totalNumero) return "";
  return `${PERCENT_FORMATTER.format((toNumber(valor) / totalNumero) * 100)}%`;
}

function criarResumoVazio() {
  return {
    funcionalidadesUtilizadas: 0,
    acessos: 0,
    usuariosDistintos: 0,
    acoesRealizadas: 0,
    entradasFuncionalidades: 0,
    tempoUsoMin: 0
  };
}

function normalizeResumoCliente(raw) {
  const cards = raw || {};
  return {
    funcionalidadesUtilizadas: toNumber(cards.funcionalidadesUsadas),
    acessos: toNumber(cards.acessos),
    usuariosDistintos: toNumber(cards.usuariosDistintos),
    acoesRealizadas: toNumber(cards.acoesRealizadas),
    entradasFuncionalidades: toNumber(cards.entradasFuncionalidades),
    tempoUsoMin: msParaMinutos(cards.tempoUsoMs)
  };
}

function normalizeFuncionalidades(raw) {
  return toArray(raw)
    .map(item => ({
      funcionalidade: String(item.funcionalidade || "").trim(),
      entradas: toNumber(item.entradas),
      acoes: toNumber(item.acoesRealizadas),
      total: toNumber(item.total)
    }))
    .sort((a, b) => b.total - a.total);
}

function formatarDataHora(valor) {
  if (valor === null || valor === undefined || valor === "") return "—";

  const raw =
    typeof valor === "number"
      ? valor
      : typeof valor === "string" && /^\d+$/.test(valor)
      ? Number(valor)
      : valor;

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "—";

  const dia = String(parsed.getDate()).padStart(2, "0");
  const mes = String(parsed.getMonth() + 1).padStart(2, "0");
  const hora = String(parsed.getHours()).padStart(2, "0");
  const minuto = String(parsed.getMinutes()).padStart(2, "0");
  return `${dia}/${mes}/${parsed.getFullYear()} ${hora}:${minuto}`;
}

function normalizeAtividades(raw) {
  return toArray(raw).map(item => ({
    data: formatarDataHora(item.dataHora),
    idUsuario: toNumber(item.idUsuario),
    texto: String(item.texto || "").trim()
  }));
}

export default {
  name: "BasesClientesTab",

  components: {
    FiltroCaracterizacaoCard,
    ClientesCard,
    SummaryCard
  },

  props: {
    isActive: { type: Boolean, default: false },
    appliedFilters: { type: Object, default: () => ({}) },
    totais: { type: Object, default: () => ({}) },
    dataInicial: { type: String, default: "" },
    dataFinal: { type: String, default: "" }
  },

  data() {
    return {
      personas: [],
      personasSelecionadas: [],
      clientes: [],
      carregandoClientes: false,
      clienteSelecionado: null,
      buscaCliente: "",
      softhouseFiltro: null,
      statusbaseFiltro: null,
      statusbaseFiltroAplicadoInicialmente: false,
      funcionalidadeSelecionada: null,
      dadosCarregados: false,
      resumoCliente: criarResumoVazio(),
      funcionalidades: [],
      atividades: [],
      carregandoResumo: false,
      carregandoAtividades: false
    };
  },

  computed: {
    socketConnections() {
      this.clientes.forEach(item => {
        const index = state.socketConnections.findIndex(
          row => item.hash_cliente === row.data.token.hash
        );
        item.statusserver = index != -1;
      });

      return state.socketConnections;
    },

    softhouseOpcoes() {
      return ["ControlSoft", "SySafra"];
    },

    statusbaseOpcoes() {
      return [
        { text: "Ativo", value: 1 },
        { text: "Inativo - sem acesso", value: 0 },
        { text: "Inativo - com acesso somente leitura", value: 2 }
      ];
    },

    personasSelecionadasIds() {
      return this.personasSelecionadas.map(persona => persona.tipoempresa);
    },

    clientesFiltradosIds() {
      const ids = toArray(this.appliedFilters.clientes)
        .map(item => (item && item.value !== undefined ? item.value : item))
        .filter(Boolean)
        .map(String);
      return ids.length ? new Set(ids) : null;
    },

    caracterizacoesFiltradasIds() {
      const ids = toArray(this.appliedFilters.caracterizacao)
        .map(item => (item && item.value !== undefined ? item.value : item))
        .filter(Boolean)
        .map(String);
      return ids.length ? new Set(ids) : null;
    },

    personasDisponiveis() {
      const ids = this.caracterizacoesFiltradasIds;
      if (!ids) return this.personas;
      return toArray(this.personas).filter(persona => ids.has(String(persona.tipoempresa)));
    },

    clientesFiltrados() {
      let lista = toArray(this.clientes);

      if (this.clientesFiltradosIds) {
        lista = lista.filter(cliente => this.clientesFiltradosIds.has(String(cliente.idcliente)));
      }

      if (this.caracterizacoesFiltradasIds) {
        const ids = this.caracterizacoesFiltradasIds;
        lista = lista.reduce((acc, cliente) => {
          const empresas = toArray(cliente.empresas).filter(empresa =>
            ids.has(String(empresa.tipoempresa))
          );
          if (empresas.length) acc.push({ ...cliente, empresas });
          return acc;
        }, []);
      }

      if (this.personasSelecionadas.length > 0) {
        const ids = this.personasSelecionadasIds;
        lista = lista.filter(
          cliente =>
            cliente.empresas && cliente.empresas.some(empresa => ids.includes(empresa.tipoempresa))
        );
      }

      if (this.softhouseFiltro) {
        lista = lista.filter(cliente => cliente.softhouse === this.softhouseFiltro);
      }

      if (this.statusbaseFiltro !== null) {
        lista = lista.filter(cliente => cliente.statusbase === this.statusbaseFiltro);
      }

      if (this.buscaCliente) {
        const busca = this.buscaCliente.toLowerCase();
        lista = lista.filter(cliente => {
          if ((cliente.nome || "").toLowerCase().includes(busca)) return true;
          const hash = cliente.hash_cliente ?? cliente.hashCliente ?? cliente.hash ?? "";
          return String(hash)
            .toLowerCase()
            .includes(busca);
        });
      }

      return lista;
    },

    summaryCards() {
      const resumo = this.resumoCliente;
      const totais = this.totais || {};
      return [
        {
          key: "funcionalidades-utilizadas",
          title: "Funcionalidades utilizadas",
          value: formatInteger(resumo.funcionalidadesUtilizadas),
          percentage: formatPercentual(
            resumo.funcionalidadesUtilizadas,
            totais.funcionalidadesUtilizadas
          )
        },
        {
          key: "acessos",
          title: "Acessos",
          value: formatInteger(resumo.acessos),
          percentage: formatPercentual(resumo.acessos, totais.acessos),
          variant: "success"
        },
        {
          key: "usuarios-distintos",
          title: "Usuários",
          value: formatInteger(resumo.usuariosDistintos),
          percentage: formatPercentual(resumo.usuariosDistintos, totais.usuariosDistintos),
          variant: "primary"
        },
        {
          key: "acoes-realizadas",
          title: "Ações realizadas",
          value: formatInteger(resumo.acoesRealizadas),
          percentage: formatPercentual(resumo.acoesRealizadas, totais.acoesRealizadas)
        },
        {
          key: "entradas-funcionalidades",
          title: "Entradas nas funcionalidades",
          value: formatInteger(resumo.entradasFuncionalidades),
          percentage: formatPercentual(
            resumo.entradasFuncionalidades,
            totais.entradasFuncionalidades
          )
        },
        {
          key: "tempo-de-uso",
          title: "Tempo de uso (min)",
          value: formatMinutos(resumo.tempoUsoMin),
          percentage: formatPercentual(resumo.tempoUsoMin, totais.tempoUsoMin)
        }
      ];
    },

    atividadesHeaders() {
      return [
        { text: "Data", value: "data", width: "150px" },
        { text: "Usuário", value: "idUsuario", width: "90px" },
        { text: "Detalhe", value: "texto" }
      ];
    },

    maiorTotalFuncionalidade() {
      return this.funcionalidades.reduce((maior, item) => Math.max(maior, toNumber(item.total)), 0);
    }
  },

  watch: {
    socketConnections() {},

    isActive: {
      immediate: true,
      handler(ativo) {
        if (ativo && !this.dadosCarregados) {
          this.dadosCarregados = true;
          this.carregarPersonasPadrao();
        }
      }
    },

    funcionalidadeSelecionada() {
      this.getAtividades();
    },

    dataInicial() {
      this.getResumoCliente();
    },

    dataFinal() {
      this.getResumoCliente();
    },

    appliedFilters() {
      const disponiveis = this.personasDisponiveis;

      // Se as caracterizações disponíveis mudaram, volta ao padrão: todas selecionadas
      const chaveDisponiveis = disponiveis.map(p => p.tipoempresa).join(",");
      if (this._chaveDisponiveis !== undefined && this._chaveDisponiveis !== chaveDisponiveis) {
        this._chaveDisponiveis = chaveDisponiveis;
        this.selecionarTodasPersonas();
        return;
      }
      this._chaveDisponiveis = chaveDisponiveis;

      const restantes = this.personasSelecionadas.filter(persona => disponiveis.includes(persona));
      if (restantes.length !== this.personasSelecionadas.length) {
        this.personasSelecionadas = restantes;
        if (restantes.length === 0) {
          this.softhouseFiltro = null;
          this.statusbaseFiltro = null;
        }
        this.clienteSelecionado = null;
        this.limparDadosCliente();
        this.getClientes();
        return;
      }

      const ids = this.clientesFiltradosIds;
      if (ids && this.clienteSelecionado && !ids.has(String(this.clienteSelecionado.idcliente))) {
        this.clienteSelecionado = null;
        this.limparDadosCliente();
        return;
      }
      this.getResumoCliente();
    }
  },

  created() {
    this._resumoRequestId = 0;
    this._atividadesRequestId = 0;
  },

  methods: {
    formatInteger,

    async getPersonas() {
      try {
        const { data } = await csapi.get("/v1/empresa/caracterizacoes");
        this.personas = data || [];
      } catch (error) {
        this.$toast.error("Erro ao carregar caracterizações");
        this.personas = [];
      }
    },

    // Por padrão todas as caracterizações vêm selecionadas e a busca é automática
    async carregarPersonasPadrao() {
      await this.getPersonas();
      this._chaveDisponiveis = this.personasDisponiveis.map(p => p.tipoempresa).join(",");
      this.selecionarTodasPersonas();
    },

    selecionarTodasPersonas() {
      this.statusbaseFiltroAplicadoInicialmente = false;
      this.personasSelecionadas = [...this.personasDisponiveis];

      if (this.personasSelecionadas.length === 0) {
        this.softhouseFiltro = null;
        this.statusbaseFiltro = null;
      } else {
        this.statusbaseFiltro = 1;
      }

      this.clienteSelecionado = null;
      this.limparDadosCliente();

      if (this.personasSelecionadas.length > 0) {
        this.getClientes();
      } else {
        this.clientes = [];
      }
    },

    async getClientes() {
      this.clientes = [];

      // Sem caracterização selecionada a lista fica escondida: não busca todos os clientes à toa.
      if (this.personasSelecionadas.length === 0) {
        this.carregandoClientes = false;
        return;
      }

      this.carregandoClientes = true;

      try {
        const params = { somenteAtivos: false };
        if (this.personasSelecionadas.length > 0) {
          params.tipoempresa = this.personasSelecionadasIds.join(",");
        }
        const { data } = await csapi.get("/v1/cliente/com-empresas", { params });

        const clientes = data || [];
        this.clientes = clientes.map(c => ({
          ...c,
          statusserver: false,
          empresas: (c.empresas || []).map(e => ({ ...e }))
        }));

        if (!this.statusbaseFiltroAplicadoInicialmente && this.clientes.length > 0) {
          this.statusbaseFiltro = 1;
          this.statusbaseFiltroAplicadoInicialmente = true;
        }
      } catch (error) {
        this.$toast.error("Erro ao carregar clientes");
        this.clientes = [];
      } finally {
        this.carregandoClientes = false;
      }
    },

    togglePersona(persona) {
      this.statusbaseFiltroAplicadoInicialmente = false;

      const index = this.personasSelecionadas.indexOf(persona);
      if (index === -1) {
        this.personasSelecionadas.push(persona);
      } else {
        this.personasSelecionadas.splice(index, 1);
      }

      if (this.personasSelecionadas.length === 0) {
        this.softhouseFiltro = null;
        this.statusbaseFiltro = null;
      } else {
        this.statusbaseFiltro = 1;
      }

      this.clienteSelecionado = null;
      this.limparDadosCliente();

      this.getClientes();
    },

    toggleSofthouseFiltro(value) {
      this.softhouseFiltro = this.softhouseFiltro === value ? null : value;
    },

    toggleStatusbaseFiltro(value) {
      this.statusbaseFiltro = this.statusbaseFiltro === value ? null : value;
    },

    limparDadosCliente() {
      this.funcionalidadeSelecionada = null;
      this.resumoCliente = criarResumoVazio();
      this.funcionalidades = [];
      this.atividades = [];
    },

    buildParamsBase() {
      const toValues = key =>
        toArray(this.appliedFilters[key])
          .map(item => (item && item.value !== undefined ? item.value : item))
          .filter(Boolean);

      const params = {
        dataInicial: this.dataInicial,
        dataFinal: this.dataFinal
      };

      const caracterizacoes = toValues("caracterizacao")
        .map(id => CARACTERIZACAO_SLUGS[Number(id)])
        .filter(Boolean);
      const plataformas = toValues("plataformas");
      const funcionalidades = toValues("funcionalidades");

      if (caracterizacoes.length) params.caracterizacoes = caracterizacoes;
      if (plataformas.length) params.plataformas = plataformas;
      if (funcionalidades.length) params.funcionalidades = funcionalidades;

      return params;
    },

    async getResumoCliente() {
      if (!this.clienteSelecionado) return;

      const requestId = ++this._resumoRequestId;
      this.carregandoResumo = true;
      this.carregandoAtividades = true;
      this.atividades = [];

      try {
        const params = {
          ...this.buildParamsBase(),
          clientes: [Number(this.clienteSelecionado.idcliente)]
        };
        const [cardsResponse, funcionalidadesResponse] = await Promise.all([
          csapi.post(ENDPOINT_CARDS, params),
          csapi.post(ENDPOINT_FUNCIONALIDADES, params)
        ]);
        if (requestId !== this._resumoRequestId) return;

        this.resumoCliente = normalizeResumoCliente(cardsResponse.data);
        this.funcionalidades = normalizeFuncionalidades(
          (funcionalidadesResponse.data || {}).funcionalidades
        );

        const primeira = this.funcionalidades[0];
        const nome = primeira ? primeira.funcionalidade : null;
        const mudou = this.funcionalidadeSelecionada !== nome;
        this.funcionalidadeSelecionada = nome;
        if (!mudou) this.getAtividades();
      } catch (error) {
        if (requestId !== this._resumoRequestId) return;
        this.$toast.error("Erro ao carregar os dados do cliente");
        this.resumoCliente = criarResumoVazio();
        this.funcionalidades = [];
        this.funcionalidadeSelecionada = null;
        this.carregandoAtividades = false;
      } finally {
        if (requestId === this._resumoRequestId) {
          this.carregandoResumo = false;
        }
      }
    },

    async getAtividades() {
      if (!this.clienteSelecionado || !this.funcionalidadeSelecionada) {
        this.atividades = [];
        this.carregandoAtividades = false;
        return;
      }

      const requestId = ++this._atividadesRequestId;
      this.carregandoAtividades = true;

      try {
        const params = {
          ...this.buildParamsBase(),
          clientes: [Number(this.clienteSelecionado.idcliente)],
          funcionalidades: [this.funcionalidadeSelecionada]
        };
        const { data } = await csapi.post(ENDPOINT_ATIVIDADES_RECENTES, params);
        if (requestId !== this._atividadesRequestId) return;

        const payload = data || {};
        this.atividades = normalizeAtividades(payload.atividades);
      } catch (error) {
        if (requestId !== this._atividadesRequestId) return;
        this.$toast.error("Erro ao carregar as atividades recentes");
        this.atividades = [];
      } finally {
        if (requestId === this._atividadesRequestId) {
          this.carregandoAtividades = false;
        }
      }
    },

    selecionarCliente(cliente) {
      this.clienteSelecionado = cliente;
      this.limparDadosCliente();
      this.getResumoCliente();
    },

    selecionarFuncionalidade(item) {
      if (!item) return;
      this.funcionalidadeSelecionada = item.funcionalidade;
    },

    larguraBarra(item) {
      const maior = this.maiorTotalFuncionalidade;
      if (!maior) return 0;
      return Math.round((toNumber(item.total) / maior) * 100);
    }
  }
};
</script>

<style scoped>
.monitoragroweb-clientes__grid {
  margin: 0 -6px !important;
}

.monitoragroweb-clientes__col--esquerda,
.monitoragroweb-clientes__col--direita {
  padding: 0 6px !important;
}

.monitoragroweb-clientes__linha-1 {
  min-height: 186px;
  height: 186px;
  max-height: 186px;
}

.monitoragroweb-clientes__flex {
  min-height: 654px;
  height: 654px;
  max-height: 654px;
}

.monitoragroweb-row {
  flex: 0 0 auto;
  margin-left: -6px !important;
  margin-right: -6px !important;
}

.monitoragroweb-row > [class*="col-"] {
  padding: 6px !important;
}

.panel-card {
  border-color: #d9e2ef !important;
  box-shadow: 0 2px 6px rgba(15, 42, 77, 0.08);
  border-radius: 18px 6px 18px 6px !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-card--identificacao {
  min-height: 76px;
  height: 76px;
  max-height: 76px;
}

.panel-card--lista {
  min-height: 654px;
  height: 654px;
  max-height: 654px;
}

.panel-card__header {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 16px 16px 0;
}

.panel-card--identificacao .panel-card__header {
  padding: 14px 16px;
}

.panel-card__title {
  color: #0a1f44;
  flex: 1 1 auto;
  font-size: 16px;
  font-weight: 700;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.panel-card__content {
  flex: 1 1 0;
  min-height: 0;
  padding-bottom: 16px !important;
}

.panel-card__content--lista,
.panel-card__content--tabela {
  overflow-y: auto;
  padding-top: 12px !important;
  scrollbar-width: thin;
}

.panel-card__empty {
  align-items: center;
  color: #5f6b7a;
  display: flex;
  height: 100%;
  justify-content: center;
  min-height: 200px;
  text-align: center;
}

.monitoragroweb-clientes__identificacao {
  min-width: 0;
}

.monitoragroweb-clientes__nome {
  color: #0a1f44;
  font-size: 16px;
  font-weight: 700;
  text-transform: uppercase;
}

.monitoragroweb-clientes__subtitulo {
  color: #7a8fa6;
  font-size: 12px;
  margin-top: 2px;
}

.func-item {
  border: 1px solid #e3eaf3;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 8px;
  padding: 10px 12px;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.func-item:hover {
  background-color: #f5f9ff;
}

.func-item--ativo {
  background-color: #eaf2ff;
  border-color: #3d82f6;
}

.func-item__header {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
}

.func-item__nome {
  color: #0a1f44;
  font-size: 13px;
  font-weight: 600;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.func-item__total {
  color: #0a1f44;
  font-size: 13px;
  font-weight: 700;
}

.func-item__bar {
  background-color: #e3eaf3;
  border-radius: 999px;
  height: 6px;
  margin-top: 8px;
  overflow: hidden;
}

.func-item__bar-fill {
  background-color: #1273b8;
  border-radius: 999px;
  height: 100%;
}

.func-item__meta {
  color: #7a8fa6;
  font-size: 11px;
  margin-top: 6px;
}

.monitoragroweb-clientes__tabela {
  max-width: 100%;
}

.monitoragroweb-clientes__tabela ::v-deep .v-data-table__wrapper {
  max-width: 100%;
  overflow-x: auto;
}

@media (max-width: 959px) {
  .monitoragroweb-clientes__linha-1 {
    height: auto;
    min-height: 140px;
    max-height: none;
  }

  .monitoragroweb-clientes__flex {
    height: auto;
    min-height: 360px;
    max-height: none;
  }

  .panel-card--lista {
    height: auto;
    min-height: 360px;
    max-height: none;
  }
}
</style>
